import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import {
  Users, Zap, Copy, CheckCircle, Clock, TrendingUp,
  DollarSign, Shield, AlertCircle, RefreshCw, ChevronRight,
  Lock, Gift, Network, Star, ArrowUpRight, Info
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  signed_up:      { label: "Signed Up",      color: "text-blue-400 bg-blue-500/10 border-blue-500/20",     icon: Clock },
  paying:         { label: "Active",          color: "text-[#D4AF37] bg-[#D4AF37]/10 border-[#D4AF37]/20", icon: CheckCircle },
  reward_unlocked:{ label: "Reward Unlocked", color: "text-[#00FF88] bg-emerald-500/10 border-emerald-500/20", icon: Star },
  pending:        { label: "Pending",         color: "text-white/40 bg-white/[0.04] border-white/[0.08]",   icon: Clock },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold border rounded-full ${cfg.color}`}>
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

function StatCard({ label, value, sub, color = "text-white", icon: Icon }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-white/30 uppercase tracking-widest font-semibold mb-2">{label}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-white/30 text-xs mt-1">{sub}</p>}
        </div>
        {Icon && (
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color === "text-[#D4AF37]" ? "bg-[#D4AF37]/10" : color === "text-[#00FF88]" ? "bg-emerald-500/10" : "bg-white/[0.04]"}`}>
            <Icon className={`w-4 h-4 ${color}`} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function NetworkCreditsPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [applyingCredits, setApplyingCredits] = useState(false);

  const appUrl = window.location.origin;

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/referrals/my-network`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error("Failed to load Network Credits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDashboard(); }, []);

  const handleGenerateCode = async () => {
    setGenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/referrals/generate-code`, {}, { withCredentials: true });
      toast.success("Your referral link is ready");
      fetchDashboard();
    } catch (err) {
      toast.error("Failed to generate code");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (!data?.referral_link) return;
    const fullLink = `${appUrl}${data.referral_link}`;
    navigator.clipboard.writeText(fullLink).then(() => {
      setCopied(true);
      toast.success("Referral link copied");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleApplyCredits = async (purpose) => {
    if (!data?.credits?.available || data.credits.available <= 0) {
      toast.error("No credits available");
      return;
    }
    const maxForPurpose = purpose === "subscription" ? data.credits.max_monthly_credit : data.credits.available;
    const amount = Math.min(data.credits.available, maxForPurpose);

    setApplyingCredits(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/referrals/apply-credits`,
        { purpose, amount },
        { withCredentials: true }
      );
      toast.success(res.data.message);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply credits");
    } finally {
      setApplyingCredits(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-black">
        <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
        <main className={mainClass}>
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-6 h-6 text-white/20 animate-spin" />
          </div>
        </main>
      </div>
    );
  }

  const credits = data?.credits || {};
  const stats = data?.stats || {};
  const referrals = data?.referrals || [];
  const history = data?.credit_history || [];

  const capProgress = Math.min(100, ((credits.total_earned || 0) / 200) * 100);
  const referralProgress = Math.min(100, ((stats.rewarded || 0) / 8) * 100);
  const fullLink = data?.referral_link ? `${appUrl}${data.referral_link}` : null;

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      <main className={mainClass}>
        <div className="p-6 max-w-6xl">
          <div className="space-y-8 max-w-5xl" data-testid="network-credits-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Network className="w-5 h-5 text-[#D4AF37]" />
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-widest font-semibold">DealForge Network Credits</p>
        </div>
        <h1 className="text-3xl font-bold text-white">Build Your Network</h1>
        <p className="text-white/40 mt-2 text-sm max-w-lg leading-relaxed">
          Invite serious operators into the ecosystem and earn DealForge Credits.
          Credits apply toward SMS, dialer usage, and platform costs.
        </p>
        <p className="text-white/60 mt-2 text-sm italic">"Serious operators don't just use the network — they build it."</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Referred" value={stats.total_referrals || 0} icon={Users} sub="all time" />
        <StatCard label="Pending" value={stats.pending || 0} icon={Clock} sub="awaiting 2nd payment" />
        <StatCard label="Credits Earned" value={`$${(credits.total_earned || 0).toFixed(0)}`} color="text-[#D4AF37]" icon={DollarSign} sub={`of $200 lifetime cap`} />
        <StatCard label="Available Now" value={`$${(credits.available || 0).toFixed(2)}`} color="text-[#00FF88]" icon={Zap} sub="ready to apply" />
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left column: Link + Credit Usage */}
        <div className="col-span-1 space-y-5">
          {/* Referral Link */}
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Gift className="w-4 h-4 text-[#D4AF37]" />
              <h3 className="text-white font-semibold text-sm">Your Referral Link</h3>
            </div>

            {!data?.referral_code ? (
              <div className="space-y-3">
                <p className="text-white/40 text-xs">Generate your unique referral link to start inviting operators.</p>
                <Button
                  onClick={handleGenerateCode}
                  disabled={generating}
                  className="w-full bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
                  data-testid="generate-code-btn"
                >
                  {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Network className="w-4 h-4 mr-2" />}
                  Generate Link
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-3">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Your Code</p>
                  <p className="text-[#D4AF37] font-mono font-bold text-lg">{data.referral_code}</p>
                </div>
                <div className="bg-white/[0.02] border border-white/[0.04] rounded p-2 break-all">
                  <p className="text-white/40 text-[10px] leading-relaxed font-mono">
                    {fullLink}
                  </p>
                </div>
                <Button
                  onClick={handleCopy}
                  className={`w-full font-semibold transition-all ${copied ? "bg-[#00FF88] text-black" : "bg-[#D4AF37] hover:bg-[#C4A030] text-black"}`}
                  data-testid="copy-link-btn"
                >
                  {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? "Copied!" : "Copy Link"}
                </Button>
              </div>
            )}
          </div>

          {/* Cap Progress */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-white/40" />
              <h3 className="text-white font-semibold text-sm">Credit Caps</h3>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-white/40">Lifetime Earned</span>
                <span className="text-white font-medium">${(credits.total_earned || 0).toFixed(0)} / $200</span>
              </div>
              <div className="w-full bg-white/[0.06] rounded-full h-1.5">
                <div className="bg-[#D4AF37] h-1.5 rounded-full transition-all" style={{ width: `${capProgress}%` }} />
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-white/40">Rewarded Referrals</span>
                <span className="text-white font-medium">{stats.rewarded || 0} / 8</span>
              </div>
              <div className="w-full bg-white/[0.06] rounded-full h-1.5">
                <div className="bg-[#00FF88] h-1.5 rounded-full transition-all" style={{ width: `${referralProgress}%` }} />
              </div>
            </div>

            {stats.cap_reached && (
              <div className="flex items-start gap-2 p-2.5 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20 rounded-lg">
                <Info className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <p className="text-[#D4AF37] text-xs">Credit cap reached. You can still refer — your network grows even after the cap.</p>
              </div>
            )}
          </div>

          {/* Apply Credits */}
          {(credits.available || 0) > 0 && (
            <div className="bg-[#0A0A0A] border border-[#00FF88]/20 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#00FF88]" />
                <h3 className="text-white font-semibold text-sm">Apply Credits</h3>
                <span className="ml-auto text-[#00FF88] font-bold text-sm">${(credits.available || 0).toFixed(2)}</span>
              </div>

              <p className="text-white/30 text-xs">Credits are platform-use only — non-transferable, non-withdrawable.</p>

              <div className="space-y-2">
                {[
                  { purpose: "sms", label: "SMS Usage", sub: "Apply to SMS costs", icon: "📱" },
                  { purpose: "dialer", label: "Dialer Usage", sub: "Apply to call costs", icon: "📞" },
                  { purpose: "subscription", label: "Subscription", sub: `Max $${credits.max_monthly_credit?.toFixed(2) || "49.50"}/mo (50%)`, icon: "⚡" },
                ].map(({ purpose, label, sub, icon }) => (
                  <button
                    key={purpose}
                    onClick={() => handleApplyCredits(purpose)}
                    disabled={applyingCredits}
                    className="w-full flex items-center gap-3 p-3 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] rounded-lg text-left transition-all disabled:opacity-50"
                    data-testid={`apply-credits-${purpose}`}
                  >
                    <span className="text-base">{icon}</span>
                    <div className="flex-1">
                      <p className="text-white text-xs font-medium">{label}</p>
                      <p className="text-white/30 text-[10px]">{sub}</p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-white/20" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Referral Table + History */}
        <div className="col-span-2 space-y-5">
          {/* How it Works */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5">
            <h3 className="text-white font-semibold text-sm mb-4">How Credits Are Earned</h3>
            <div className="grid grid-cols-4 gap-3">
              {[
                { step: "1", label: "Share Link", desc: "Send your referral link to a serious operator", color: "border-white/[0.1]" },
                { step: "2", label: "They Sign Up", desc: "They register using your link", color: "border-white/[0.1]" },
                { step: "3", label: "They Pay (×2)", desc: "They complete 2 billing cycles as an active subscriber", color: "border-[#D4AF37]/20" },
                { step: "4", label: "Credits Unlock", desc: "$25 added to your balance (portal-dependent)", color: "border-[#00FF88]/20" },
              ].map(({ step, label, desc, color }) => (
                <div key={step} className={`border ${color} bg-white/[0.01] rounded-xl p-3`}>
                  <div className="w-6 h-6 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-2">
                    <span className="text-[#D4AF37] text-[10px] font-bold">{step}</span>
                  </div>
                  <p className="text-white text-xs font-semibold mb-1">{label}</p>
                  <p className="text-white/30 text-[10px] leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            {/* Credit tiers */}
            <div className="mt-4 pt-4 border-t border-white/[0.06]">
              <p className="text-[10px] text-white/30 uppercase tracking-wider font-semibold mb-3">Credit by Portal Type</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { portal: "Investor / Contractor / Realtor", amount: "$25" },
                  { portal: "Lender", amount: "$50" },
                  { portal: "Title Company", amount: "$75" },
                ].map(({ portal, amount }) => (
                  <div key={portal} className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.02] border border-white/[0.06] rounded-full">
                    <span className="text-[#D4AF37] text-xs font-bold">{amount}</span>
                    <span className="text-white/40 text-[10px]">{portal}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Referral Table */}
          <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
              <h3 className="text-white font-semibold text-sm">Your Referrals ({referrals.length})</h3>
              {stats.remaining_reward_slots > 0 && (
                <span className="text-[10px] text-white/30">{stats.remaining_reward_slots} reward slot{stats.remaining_reward_slots !== 1 ? "s" : ""} remaining</span>
              )}
            </div>

            {referrals.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-10 h-10 text-white/[0.08] mx-auto mb-3" />
                <p className="text-white/30 text-sm">No referrals yet</p>
                <p className="text-white/20 text-xs mt-1">Share your link to invite operators into the network</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                <div className="grid grid-cols-4 px-5 py-2.5 text-[10px] text-white/20 uppercase tracking-wider font-semibold">
                  <span>Operator</span>
                  <span>Signed Up</span>
                  <span>Status</span>
                  <span className="text-right">Credit</span>
                </div>
                {referrals.map((ref) => (
                  <div key={ref.referral_id} className="grid grid-cols-4 items-center px-5 py-3.5 hover:bg-white/[0.01] transition-all" data-testid={`referral-row-${ref.referral_id}`}>
                    <div>
                      <p className="text-white text-xs font-medium truncate">{ref.referred_name || "—"}</p>
                      <p className="text-white/30 text-[10px] truncate">{ref.referred_email}</p>
                    </div>
                    <span className="text-white/40 text-xs">{ref.signup_date ? new Date(ref.signup_date).toLocaleDateString() : "—"}</span>
                    <StatusBadge status={ref.status} />
                    <div className="text-right">
                      {ref.status === "reward_unlocked" ? (
                        <span className="text-[#00FF88] font-bold text-sm">+${ref.credit_amount?.toFixed(0)}</span>
                      ) : (
                        <span className="text-white/20 text-xs">${ref.credit_amount?.toFixed(0)} pending</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Credit History */}
          {history.length > 0 && (
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/[0.06]">
                <h3 className="text-white font-semibold text-sm">Credit History</h3>
              </div>
              <div className="divide-y divide-white/[0.04]">
                {history.slice().reverse().map((txn, i) => (
                  <div key={txn.txn_id || i} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${txn.type === "earned" ? "bg-[#00FF88]/10" : "bg-white/[0.04]"}`}>
                        {txn.type === "earned" ? <ArrowUpRight className="w-3.5 h-3.5 text-[#00FF88]" /> : <Zap className="w-3.5 h-3.5 text-[#D4AF37]" />}
                      </div>
                      <div>
                        <p className="text-white text-xs">{txn.description}</p>
                        <p className="text-white/30 text-[10px]">{txn.date ? new Date(txn.date).toLocaleDateString() : ""}</p>
                      </div>
                    </div>
                    <span className={`font-bold text-sm ${txn.amount > 0 ? "text-[#00FF88]" : "text-white/50"}`}>
                      {txn.amount > 0 ? `+$${txn.amount.toFixed(2)}` : `-$${Math.abs(txn.amount).toFixed(2)}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer disclaimer */}
      <div className="flex items-start gap-3 p-4 bg-white/[0.01] border border-white/[0.04] rounded-xl">
        <Shield className="w-4 h-4 text-white/20 flex-shrink-0 mt-0.5" />
        <p className="text-white/20 text-xs leading-relaxed">
          DealForge Credits are non-transferable, non-withdrawable, and have no cash value.
          Credits expire if account is cancelled. Maximum $49.50 per month may be applied toward subscription.
          Credits are issued only after the referred user completes their second billing cycle.
          Lifetime cap: $200 (8 rewarded referrals). The more you contribute to the network, the more efficiently you can operate.
        </p>
      </div>
    </div>
        </div>
      </main>
    </div>
  );
}
